import type { CostDashboardFiltersResponse, CostDashboardResponse, CostPayload, CostSummaryState, ResourcegroupLevelCostDataPayload, ResourcegroupLevelCostDataResponse, SubscriptionForCostResponse } from "../types/summary.types";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import type { RootState } from "@/store/store";
import { costService } from "../services/costService";
import {
  buildDashboardPayloadKey,
  buildFiltersPayloadKey,
} from "../utils/shared/costRequestKey.utils";
import { getErrorMessage, isAbortError } from "../utils/shared/error.utils";
import type { CostDashboardFiltersPayload } from "../types/summary.types";

/**
 * State for dashboard summary, filters, subscriptions, and resource group drilldown
 */
const initialState: CostSummaryState = {
  // Dashboard
  dashboardLoading: false,
  dashboardError: null,
  dashboardData: null,

  // Subscriptions
  subscriptions: [],
  subscriptionsLoading: false,
  subscriptionsError: null,

  // Filters
  customerRgFilters: null,
  customerRgFiltersLoading: false,
  customerRgFilterError: null,

  // Resource group drilldown
  resourceGroupLevelData: null,
  resourceGroupLevelDataLoading: false,
  resourceGroupLevelDataError: null,
  subscriptionsStatus: "idle",
  currentDashboardRequestId: null,
};

// Abort controller for dashboard requests
let costAbortController: AbortController | null = null;
let filtersAbortController: AbortController | null = null;
let lastDashboardPayloadKey: string | null = null;
let lastFiltersPayloadKey: string | null = null;
let activeDashboardRequestId: string | null = null;
let activeFiltersRequestId: string | null = null;

/* ===================== DASHBOARD ===================== */

/**
 * Fetches cost dashboard summary data including KPIs and charts.
 * Cancels any in-flight request before starting new one.
 * Skips duplicate requests based on payload comparison.
 * @param payload - Dashboard request with time range and filters
 * @returns Dashboard response with KPI, trends, and breakdown data
 */
export const fetchCostDashboard = createAsyncThunk<
  CostDashboardResponse,
  CostPayload,
  { state: RootState; rejectValue: string }
>(
  "costSummary/fetchCostDashboard",
  async (payload, { rejectWithValue }) => {
    try {
      if (costAbortController) costAbortController.abort();
      costAbortController = new AbortController();

      return await costService.GetCostDashboardSummary(payload, {
        signal: costAbortController.signal,
      });
    } catch (err: unknown) {
      if (isAbortError(err)) {
        return rejectWithValue("aborted");
      }
      return rejectWithValue(getErrorMessage(err, "Something went wrong"));
    }
  },
  {
    condition: (payload, { getState }) => {
      const state = getState();
      const hasData = state.costSummary.dashboardData !== null;
      // Always fetch if no data (handles page refresh where Redux is reset but module variables persist)
      if (!hasData) {
        lastDashboardPayloadKey = null;
        return true;
      }
      const key = buildDashboardPayloadKey(payload);
      return key !== lastDashboardPayloadKey;
    },
  },
);

/* ===================== FILTERS ===================== */

/**
 * Fetches customer and resource group filter options for dashboard.
 * Cancels any in-flight request before starting new one.
 * Skips duplicate requests based on payload comparison.
 * @param payload - Filter request with subscription IDs
 * @returns Filter response with customers, resource groups, and services
 */
export const fetchCostDashboardFilters = createAsyncThunk<
  CostDashboardFiltersResponse,
  CostDashboardFiltersPayload,
  { state: RootState; rejectValue: string }
>(
  "costSummary/fetchCostDashboardFilters",
  async (payload, { rejectWithValue }) => {
    try {
      // cancel previous request
      if (filtersAbortController) {
        filtersAbortController.abort();
      }
      filtersAbortController = new AbortController();
      return await costService.GetDashboardFiltersForCost(payload, {
        signal: filtersAbortController.signal,
      });
    } catch (err: unknown) {
      if (isAbortError(err)) {
        return rejectWithValue("aborted");
      }
      return rejectWithValue(getErrorMessage(err, "Failed to load filters"));
    }
  },
  {
    condition: (payload, { getState }) => {
      const state = getState();
      // Always fetch if no filters cached (handles page refresh)
      if (state.costSummary.customerRgFilters === null) {
        lastFiltersPayloadKey = null;
        return true;
      }
      const key = buildFiltersPayloadKey(payload);
      return key !== lastFiltersPayloadKey;
    },
  },
);

/* ===================== RESOURCE GROUP ===================== */

/**
 * Fetches detailed cost breakdown for a specific resource group.
 * Used for drilldown from summary charts.
 * @param payload - Request with resource group and time range
 * @returns Cost data broken down by resource within the group
 */
export const fetchResourceGroupLevelCost = createAsyncThunk<
  ResourcegroupLevelCostDataResponse,
  ResourcegroupLevelCostDataPayload,
  { rejectValue: string }
>(
  "costSummary/fetchResourceGroupLevelCost",
  async (payload, { rejectWithValue }) => {
    try {
      return await costService.GetResourceGroupLevelCost(payload);
    } catch (err: unknown) {
      return rejectWithValue(
        getErrorMessage(err, "Failed to load Resource Group data"),
      );
    }
  },
);

/* ===================== SUBSCRIPTIONS ===================== */

/**
 * Fetches available Azure subscriptions for cost filtering.
 * Called once on initial load and cached in Redux.
 * @returns Array of subscription objects with ID and short name
 */
export const fetchCostSubscriptions = createAsyncThunk<
  SubscriptionForCostResponse[],
  void,
  { state: RootState; rejectValue: string }
>(
  "costSummary/fetchSubscriptions",
  async (_, { rejectWithValue }) => {
    try {
      return await costService.GetSubscriptionDetailsForCost();
    } catch (err: unknown) {
      return rejectWithValue(
        getErrorMessage(err, "Failed to load subscriptions"),
      );
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();
      const status = state.costSummary.subscriptionsStatus;
      return status !== "loading" && status !== "succeeded";
    },
  },
);

/* ===================== SLICE ===================== */
const costSummarySlice = createSlice({
  name: "costSummary",
  initialState,
  reducers: {
    clearResourceGroupLevelData: (state) => {
      state.resourceGroupLevelData = null;
      state.resourceGroupLevelDataLoading = false;
      state.resourceGroupLevelDataError = null;
    },
  },
  extraReducers: (builder) => {
    /* DASHBOARD */
    builder
      .addCase(fetchCostDashboard.pending, (state, action) => {
        activeDashboardRequestId = action.meta.requestId;
        state.dashboardLoading = true;
        state.dashboardError = null;
      })
      .addCase(fetchCostDashboard.fulfilled, (state, action) => {
        if (action.meta.requestId !== activeDashboardRequestId) {
          return;
        }

        activeDashboardRequestId = null;
        state.dashboardLoading = false;
        state.dashboardData = action.payload;
        lastDashboardPayloadKey = buildDashboardPayloadKey(action.meta.arg);
      })
      .addCase(fetchCostDashboard.rejected, (state, action) => {
        if (action.meta.requestId !== activeDashboardRequestId) {
          return;
        }

        activeDashboardRequestId = null;
        state.dashboardLoading = false;

        if (action.payload === "aborted") {
          return;
        }

        state.dashboardError =
          action.payload || action.error.message || "Failed to load dashboard";
      });

    /* SUBSCRIPTIONS */
    builder
      .addCase(fetchCostSubscriptions.pending, (state) => {
        state.subscriptionsLoading = true;
        state.subscriptionsError = null;
        state.subscriptionsStatus = "loading";
      })
      .addCase(fetchCostSubscriptions.fulfilled, (state, action) => {
        state.subscriptions = action.payload;
        state.subscriptionsLoading = false;
        state.subscriptionsStatus = "succeeded";
      })
      .addCase(fetchCostSubscriptions.rejected, (state, action) => {
        state.subscriptionsLoading = false;
        state.subscriptionsStatus = "failed";
        if (action.payload === "aborted") {
          return;
        }

        state.subscriptionsError =
          action.payload ||
          action.error.message ||
          "Failed to load subscriptions";
      });

    /* FILTERS */
    builder
      .addCase(fetchCostDashboardFilters.pending, (state, action) => {
        activeFiltersRequestId = action.meta.requestId;
        state.customerRgFiltersLoading = true;
        state.customerRgFilterError = null;
      })
      .addCase(fetchCostDashboardFilters.fulfilled, (state, action) => {
        if (action.meta.requestId !== activeFiltersRequestId) {
          return;
        }

        activeFiltersRequestId = null;
        state.customerRgFiltersLoading = false;
        state.customerRgFilters = action.payload;
        lastFiltersPayloadKey = buildFiltersPayloadKey(action.meta.arg);
      })
      .addCase(fetchCostDashboardFilters.rejected, (state, action) => {
        if (action.meta.requestId !== activeFiltersRequestId) {
          return;
        }

        activeFiltersRequestId = null;
        state.customerRgFiltersLoading = false;

        if (action.payload === "aborted") {
          return;
        }

        state.customerRgFilterError =
          action.payload || "Failed to load customers";
      });

    /* RESOURCE GROUP */
    builder
      .addCase(fetchResourceGroupLevelCost.pending, (state) => {
        state.resourceGroupLevelDataLoading = true;
        state.resourceGroupLevelDataError = null;
        state.resourceGroupLevelData = null;
      })
      .addCase(fetchResourceGroupLevelCost.fulfilled, (state, action) => {
        state.resourceGroupLevelData = action.payload;
        state.resourceGroupLevelDataLoading = false;
      })
      .addCase(fetchResourceGroupLevelCost.rejected, (state, action) => {
        state.resourceGroupLevelDataLoading = false;
        state.resourceGroupLevelDataError =
          action.payload || "Failed to load Resource Group filters";
      });
  },
});

export const { clearResourceGroupLevelData } = costSummarySlice.actions;
export default costSummarySlice.reducer;
